import { useEffect, useState } from 'react';
import { Button, useTheme } from '@mui/material';
import { SettingsIconV2 } from 'src/assets/icons/SettingsIconV2';
import { useAuthentication } from 'src/features/authentication/context';
import { useControllersContext } from 'src/features/sites/contexts/controllersContext';
import { ControllerWidgetModal } from '../Controller/style';
import { LocalStorageService } from 'src/features/authentication/utils';
import { ControllerModalContent } from '../ControllerModalContent';
import { useActiveMode } from '../../hooks/useActiveMode';
import { useResponsive } from 'src/hooks/useResponsive';
import { useControllerAdditionalInfo } from '../../hooks/useControllerAdditionalInfo';
import { useController } from '../../hooks/useController';

type Props = {
  controllerId: string;
  gatewayId?: string;
  title?: string;
  hideText?: boolean;
  isOpen: boolean;
  setControllerState: any;
  testId?: string;
};

export const ControllerElement = ({
  gatewayId,
  controllerId,
  title,
  hideText = false,
  isOpen,
  setControllerState,
  testId,
}: Props) => {
  const theme = useTheme();
  const [confirmDialogState, setConfirmDialogState] = useState<boolean>(false);
  const [tagIdsForUpdate, setTagIdsForUpdate] = useState<string[]>([]);
  const { getControllers, controllers } = useControllersContext();

  const findCurrentController = controllers?.find((controller) => controller.id === controllerId);
  const activeMode = findCurrentController?.activeMode;

  const gatewayIdValue = gatewayId ? gatewayId : findCurrentController?.gatewayId;

  const isMobile = useResponsive('down', 'md');

  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const onConfirmDialogStateHandler = (value: boolean) => {
    setConfirmDialogState(value);
  };
  const onControllerModalContentStateHandler = (value: boolean) => {
    setControllerState(value);
  };

  const onSetTagIdsForUpdate = (tagIds: string[]) => {
    setTagIdsForUpdate(tagIds);
  };

  const { controllerAdditionalInfo, getControllerAdditionalInfo } = useControllerAdditionalInfo({
    customerId,
    siteId,
    gatewayId: gatewayIdValue,
    controllerId,
    includeLatestValues: true,
  });

  const {
    isDataLoading,
    setIsDataLoadingHandler,
    setErrorOnUpdateControls,
    latestControl,
    latestControlsForInhand,
    isErrorLastControls,
    isErrorOnUpdateControls,
    lastUpdateReadingTimestamp,
    isAppliedChanges,
    setIsAppliedChangedHandler,
    enableApplyButtonHandler,
    disableApplyButtonHandler,
    isBtnApplyDisabled,
    isCloseBtn,
    setIsCloseBtn,
  } = useController({
    gatewayId: gatewayIdValue,
    controllerModalContentState: isOpen,
    controllerId: controllerId,
  });

  const { displayMode } = useActiveMode({
    activeMode: activeMode,
    controllerAdditionalInfo: controllerAdditionalInfo,
  });

  const onCloseHandler = () => {
    setIsCloseBtn(false);
    getControllerAdditionalInfo();
    getControllers();
    if (!isBtnApplyDisabled && !isAppliedChanges) {
      onConfirmDialogStateHandler(true);
    } else {
      disableApplyButtonHandler();
      onControllerModalContentStateHandler(false);
    }
    if (!isDataLoading) {
      setIsAppliedChangedHandler(false);
      controllerAdditionalInfo?.forEach((tag) => {
        LocalStorageService.set(tag.id, 'error-seen');
      });
    }
  };

  const onOpenHandler = () => {
    getControllers();
    setControllerState(true);
    getControllerAdditionalInfo();
  };

  useEffect(() => {
    let interval: any;

    if (!latestControlsForInhand) {
      latestControl(customerId!, gatewayIdValue!, tagIdsForUpdate!);
    }

    if (isDataLoading) {
      interval = setInterval(() => {
        latestControl(customerId!, gatewayIdValue!, tagIdsForUpdate!);
      }, 5000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [
    controllerAdditionalInfo,
    customerId,
    gatewayIdValue,
    isDataLoading,
    latestControl,
    latestControlsForInhand,
    tagIdsForUpdate,
  ]);

  return (
    <>
      <ControllerWidgetModal maxWidth={'lg'} open={isOpen} onClose={onCloseHandler}>
        <ControllerModalContent
          onClose={onCloseHandler}
          title={title ? title : findCurrentController?.controllerName!}
          controllerId={controllerId}
          gatewayId={gatewayIdValue}
          activeMode={activeMode}
          latestControl={latestControl}
          latestControlsForInhand={latestControlsForInhand}
          setIsDataLoadingHandler={setIsDataLoadingHandler}
          setErrorOnUpdateControls={setErrorOnUpdateControls}
          controllerAdditionalInfo={controllerAdditionalInfo}
          isErrorLastControls={isErrorLastControls}
          isErrorOnUpdateControls={isErrorOnUpdateControls}
          isDataLoading={isDataLoading}
          lastUpdateReadingTimestamp={lastUpdateReadingTimestamp}
          isAppliedChanges={isAppliedChanges}
          setIsAppliedChangedHandler={setIsAppliedChangedHandler}
          confirmDialogState={confirmDialogState}
          onConfirmDialogStateHandler={onConfirmDialogStateHandler}
          enableApplyButtonHandler={enableApplyButtonHandler}
          disableApplyButtonHandler={disableApplyButtonHandler}
          isBtnApplyDisabled={isBtnApplyDisabled}
          onControllerModalContentStateHandler={onControllerModalContentStateHandler}
          displayMode={displayMode}
          onSetTagIdsForUpdate={onSetTagIdsForUpdate}
          isCloseBtn={isCloseBtn}
        />
      </ControllerWidgetModal>
      <Button
        sx={{
          minWidth: hideText ? 'auto' : undefined,
          padding: hideText ? 0 : undefined,

          ...(hideText && {
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }),

          '@media (max-width:600px)': {
            fontSize: '0.8rem',
          },
        }}
        data-sm={testId ? `${testId}-control-btn` : 'control-btn'}
        variant="text"
        onClick={onOpenHandler}
        startIcon={<SettingsIconV2 fill={theme.palette.primary.main} width={isMobile ? 15 : 18} />}
      >
        {hideText ? '' : 'Control'}
      </Button>
    </>
  );
};
